import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import { useTranslation } from "react-i18next";
import {
  isCurrentDashboardFilterSaved,
  saveDashboardFilter,
} from "../../utils/dashboardSavedFilters";

/**
 * Menu da toolbar: lista filtros salvos + atalho para salvar a combinação atual
 * quando ela ainda não existe entre os salvos.
 */
export default function DashboardSavedFiltersMenu({
  anchorEl,
  open,
  onClose,
  section,
  entries = [],
  filterState,
  criteriaCount = 0,
  onApply,
  onSaved,
}) {
  const { t } = useTranslation("settings");
  const [saveName, setSaveName] = useState("");
  const [saveError, setSaveError] = useState("");

  const canSaveCurrent = useMemo(() => {
    if (!section || criteriaCount <= 0 || !filterState) return false;
    return !isCurrentDashboardFilterSaved(section, filterState);
  }, [section, criteriaCount, filterState, entries]);

  useEffect(() => {
    if (!open) {
      setSaveName("");
      setSaveError("");
    }
  }, [open]);

  const handleSave = () => {
    setSaveError("");
    try {
      saveDashboardFilter({
        section,
        nome: saveName,
        state: filterState,
        favorito: false,
      });
      setSaveName("");
      onSaved?.();
    } catch (err) {
      setSaveError(
        err?.message ||
          t("dashboardPage.savedFilters.saveError", {
            defaultValue: "Não foi possível salvar o filtro",
          })
      );
    }
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { minWidth: 260, maxWidth: 340 } }}
    >
      {entries.length === 0 && !canSaveCurrent ? (
        <MenuItem disabled>
          <ListItemText
            primary={t("dashboardPage.savedFilters.emptyShort", {
              defaultValue: "Nenhum filtro salvo",
            })}
          />
        </MenuItem>
      ) : null}

      {entries.map((entry) => (
        <MenuItem
          key={entry.id}
          onClick={() => {
            onApply?.(entry.state);
            onClose?.();
          }}
        >
          <ListItemText
            primary={entry.nome}
            primaryTypographyProps={{ noWrap: true }}
          />
        </MenuItem>
      ))}

      {canSaveCurrent ? (
        <Box sx={{ px: 1.5, pt: entries.length ? 0.5 : 0.75, pb: 1.25 }}>
          {entries.length > 0 ? <Divider sx={{ mb: 1.25 }} /> : null}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {t("dashboardPage.savedFilters.saveCurrentHint", {
              defaultValue: "Esta combinação ainda não está salva",
            })}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
            <TextField
              size="small"
              autoFocus={entries.length === 0}
              placeholder={t("dashboardPage.savedFilters.namePlaceholderShort", {
                defaultValue: "Nome do filtro",
              })}
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter" && saveName.trim()) handleSave();
              }}
              sx={{ flex: 1, minWidth: 0 }}
              inputProps={{ "aria-label": t("dashboardPage.savedFilters.namePlaceholderShort", {
                defaultValue: "Nome do filtro",
              }) }}
            />
            <Button
              size="small"
              variant="contained"
              startIcon={<BookmarkAddOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={handleSave}
              disabled={!saveName.trim()}
              sx={{ textTransform: "none", flexShrink: 0, whiteSpace: "nowrap" }}
            >
              {t("dashboardPage.savedFilters.save", { defaultValue: "Salvar" })}
            </Button>
          </Box>
          {saveError ? (
            <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>
              {saveError}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </Menu>
  );
}
